import clsx from 'clsx';
import * as React from 'react';

import { LoadingPanel } from '@gitbook/spine-react';

import { sizes, spacing } from '../constants';
import { usePortalRelativePosition } from '../Portal';
import type { LayoutDimensionsRange } from './computePopoverPosition';
import cssStyles from './Popover.module.css';
import type { PopoverProps, PopoverRef } from './types';
import { usePopoverPosition } from './usePopoverPosition';

/*
 * Required for popover calculations but duplicates CSS values.
 * TODO: use CSS and calculate based on bounding rectangle
 */
const popoverSizeDefinitions: { [K: string]: LayoutDimensionsRange } = {
    tooltip: {
        minWidth: sizes.XS,
        minHeight: sizes.XS,
        maxWidth: 500,
        maxHeight: 340,
    },
    default: {
        minWidth: sizes.XS,
        minHeight: sizes.XS,
        maxWidth: 200,
        maxHeight: 340,
    },
    large: {
        minWidth: 340,
        minHeight: sizes.XS,
        maxWidth: 340,
        maxHeight: 320,
    },
    xlarge: {
        minWidth: 500,
        minHeight: sizes.XS,
        maxWidth: 500,
        maxHeight: 1000,
    },
    wide: {
        minWidth: sizes.XS,
        minHeight: sizes.XS,
        maxWidth: 1000,
        maxHeight: 1000,
    },
    menu: {
        minWidth: sizes.XS,
        minHeight: sizes.XS,
        maxWidth: 500,
        maxHeight: 1000,
    },
};

/**
 * Popover on desktop displayed relative to the element.
 */
export const PopoverDesktop = React.forwardRef(function PopoverDesktopWithRef(
    props: PopoverProps,
    ref: React.Ref<PopoverRef>
): React.ReactElement {
    const {
        children,
        kind = 'default',
        preferredAlignment = 'middle',
        preferredPlacement = 'bottom',
        chromeless = false,
    } = props;

    // When wrapped into a PortalRelative / PortalToggeable, we skip a measurement
    // and a paint which will render the popover at the wrong position
    // by getting the latest position from the parent
    const portalRelativeRect = usePortalRelativePosition();
    const anchorRef = React.useRef(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [positionStyle, , , updatePosition] = usePopoverPosition(
        portalRelativeRect || anchorRef,
        wrapperRef,
        {
            spacingWithAnchor: spacing.small,
            preferredAlignment,
            preferredPlacement,
            range: popoverSizeDefinitions[kind],
        }
    );

    React.useImperativeHandle(
        ref,
        () => ({
            updatePosition,
        }),
        [updatePosition]
    );

    return (
        <>
            {portalRelativeRect ? null : (
                <div ref={anchorRef} className="pointer-events-none absolute inset-0" />
            )}
            <div
                ref={wrapperRef}
                data-popover-overlay="true"
                className={clsx(
                    cssStyles.popover,
                    cssStyles[kind],
                    'fade-in flex animate-in flex-col'
                )}
                style={positionStyle as React.CSSProperties}
            >
                <div
                    className={clsx(
                        cssStyles.card,
                        'flex flex-col rounded-md',
                        kind === 'tooltip' && 'border-transparent',
                        kind === 'menu' && 'rounded-lg',
                        chromeless ? [cssStyles.chromeless, 'border-transparent'] : 'border',
                        {
                            [cssStyles.tooltip]: kind === 'tooltip',
                            [cssStyles.menu]: kind === 'menu',
                        }
                    )}
                >
                    <div className={clsx('flex flex-col', cssStyles.content)}>
                        <React.Suspense fallback={<LoadingPanel size="small" />}>
                            {children}
                        </React.Suspense>
                    </div>
                </div>
            </div>
        </>
    );
});
